import AlertBanner from "components/AlertBanner";
import DeviceSelector from "components/DeviceSelector";
import DebugConfigModal from "components/modals/DebugConfigModal";
import { useDevices } from "hooks/useDevices";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Bug,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Cpu,
  Database,
  Download,
  Filter,
  HardDrive,
  Info,
  LineChart as LineChartIcon,
  RefreshCw,
  Search,
  Settings,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  ComposedChart,
  ErrorBar,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getDiagnostics, getLogs, getSystemOverview, getTelemetry, getTelemetrySeries, getRawSamples } from "services/api";
import { formatUptime, formatRelativeTime, getHealthStatusColor } from "utils/helper";
import {
  TELEMETRY_GROUPS,
  KEY_LABELS,
  formatTelemetryValue,
  deriveNumericFields,
  seriesRequestFields,
  withDerivedFields,
  withDerivedSeries,
} from "utils/telemetryFields";
import { validSamples, speedOfSound, usToCm } from "utils/rawDistanceStats";

// ──────────────────────────────────────────────────────────────────────────────
// Rich Telemetry Viewer
// Shows every field present in raw_payload, grouped into panels. Panels with
// no data on this payload are hidden, so v0.2 devices (subset of keys) render
// cleanly without empty placeholders, while v0.3 devices show all extra fields
// (ext_t/ext_h, tilt, ax/ay/az, t_sens/t_mdm/t_net/t_mqtt, solar_state, etc.).
// ──────────────────────────────────────────────────────────────────────────────

// Inline SVG sparkline + per-attempt mm conversion for d1_raw_us (h9).
// Uses calibrated speed of sound when ext_t/ext_h are present, else 343 m/s.
// Renders timeouts (0 µs) as red dots and outliers (>2σ from mean) in amber.
// Features: hover tooltip (index, µs, mm), view toggles (chart/table/histogram),
// gridlines, mm Y-axis labels, median reference line, and an "Expand 50" mode
// that renders a tall full-width chart with per-sample x labels for fine
// inspection of all 50 raw → distance points in a single telemetry instance.
const DistanceRawSparkline = ({ rawUs, payload, defaultExpanded = false, alwaysExpanded = false }) => {
  const [view, setView] = React.useState("chart"); // chart | histogram | table
  const [expandedState, setExpanded] = React.useState(defaultExpanded);
  const expanded = alwaysExpanded || expandedState;
  const [hover, setHover] = React.useState(null);  // {i, us, mm, x, y}

  if (!Array.isArray(rawUs) || rawUs.length === 0) return null;
  const ext_t = typeof payload?.ext_t === "number" ? payload.ext_t : null;
  const ext_h = typeof payload?.ext_h === "number" ? payload.ext_h : null;
  const c_ms = (ext_t !== null && ext_h !== null)
    ? 331.3 * Math.sqrt(1 + ext_t / 273.15) + 0.0124 * ext_h
    : 343.0;
  const usToMm = (us) => (us > 0) ? (us * (c_ms / 1000.0) / 2.0) : null;
  const valids = rawUs.filter((v) => typeof v === "number" && v > 0);

  if (valids.length === 0) {
    return <div className="text-[10px] text-gray-500 mt-2">All {rawUs.length} attempts timed out.</div>;
  }
  const mean = valids.reduce((a, b) => a + b, 0) / valids.length;
  const variance = valids.reduce((a, b) => a + (b - mean) ** 2, 0) / Math.max(1, valids.length - 1);
  const sd = Math.sqrt(variance);
  const sorted = [...valids].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const min = sorted[0];
  const max = sorted[sorted.length - 1];

  // Chart geometry — expanded mode is 2x tall + denser axis labels for fine inspection
  const W = expanded ? 1000 : 480;
  const H = expanded ? 320 : 140;
  const padL = expanded ? 56 : 40;
  const padR = expanded ? 12 : 8;
  const padT = expanded ? 12 : 8;
  const padB = expanded ? 28 : 18;
  const cssHeight = expanded ? "440px" : "180px";
  const ptR = expanded ? 3 : 1.5;
  const ptROut = expanded ? 4 : 2.5;
  const yTickCount = expanded ? 10 : 5;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const stepX = innerW / Math.max(1, rawUs.length - 1);
  // Pad Y range a bit so points aren't on the edge
  const ySpan = (max - min) || 1;
  const yMin = min - ySpan * 0.1;
  const yMax = max + ySpan * 0.1;
  const yRange = yMax - yMin;
  const yFor = (us) => padT + innerH - ((us - yMin) / yRange) * innerH;
  const xFor = (i) => padL + i * stepX;
  const points = rawUs.map((us, i) => ({
    x: xFor(i),
    y: us > 0 ? yFor(us) : null,
    us,
    mm: usToMm(us),
    i,
  }));
  const path = points
    .map((p, i) => p.y === null ? "" : `${i === 0 || points[i - 1].y === null ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(" ");

  // Y-axis ticks
  const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) => {
    const f = i / yTickCount;
    const us = yMin + f * yRange;
    return { us, y: yFor(us), mm: us * (c_ms / 1000.0) / 2.0 };
  });

  // X-axis labels: every sample when expanded, 5 anchors when compact
  const xLabelIndices = expanded
    ? Array.from({ length: rawUs.length }, (_, i) => i).filter((i) => i % 5 === 0 || i === rawUs.length - 1)
    : [0, Math.floor(rawUs.length / 4), Math.floor(rawUs.length / 2), Math.floor((3 * rawUs.length) / 4), rawUs.length - 1];

  // Histogram: 12 bins over [min, max] in mm
  const binCount = 12;
  const minMm = usToMm(min), maxMm = usToMm(max);
  const binW = ((maxMm - minMm) / binCount) || 1;
  const bins = new Array(binCount).fill(0);
  valids.forEach((us) => {
    const mm = usToMm(us);
    let b = Math.floor((mm - minMm) / binW);
    if (b >= binCount) b = binCount - 1;
    if (b < 0) b = 0;
    bins[b]++;
  });
  const binMax = Math.max(...bins, 1);

  const handleMove = (evt) => {
    const svg = evt.currentTarget;
    const rect = svg.getBoundingClientRect();
    // Map mouse X (client) into the SVG viewBox X coordinate
    const xVB = ((evt.clientX - rect.left) / rect.width) * W;
    // Find nearest sample
    let bestI = 0, bestD = Infinity;
    for (let i = 0; i < points.length; i++) {
      const d = Math.abs(points[i].x - xVB);
      if (d < bestD) { bestD = d; bestI = i; }
    }
    const p = points[bestI];
    setHover({ i: bestI, us: p.us, mm: p.mm, x: p.x, y: p.y });
  };

  return (
    <div className="mt-3 pt-2 border-t border-outline-variant/20">
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-wider text-on-surface-variant">
          Raw samples (µs → mm @ c={c_ms.toFixed(1)} m/s{ext_t === null && " est."})
        </div>
        <div className="flex gap-0.5 text-[10px] items-center">
          {["chart", "histogram", "table"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-2 py-0.5 rounded ${view === v ? "bg-primary text-on-primary" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            >
              {v}
            </button>
          ))}
          {view === "chart" && (
            <button
              onClick={() => setExpanded((e) => !e)}
              title="Toggle full-width 50-sample inspection chart"
              className={`ml-1 px-2 py-0.5 rounded font-semibold ${expanded ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              📈 Dist 1 all raw {expanded ? "✓" : `(${rawUs.length})`}
            </button>
          )}
        </div>
      </div>

      {view === "chart" && (
        <div className="relative">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full bg-gray-50 rounded select-none"
            style={{ height: cssHeight }}
            onMouseMove={handleMove}
            onMouseLeave={() => setHover(null)}
          >
            {/* Y gridlines + labels */}
            {yTicks.map((t, idx) => (
              <g key={idx}>
                <line x1={padL} y1={t.y} x2={W - padR} y2={t.y} stroke="#e5e7eb" strokeDasharray="2,2" strokeWidth="0.5" />
                <text x={padL - 4} y={t.y + 3} textAnchor="end" fontSize={expanded ? 10 : 8} fill="#6b7280" fontFamily="monospace">
                  {t.mm.toFixed(1)}
                </text>
              </g>
            ))}
            {/* Y-axis title (mm) */}
            {expanded && (
              <text x={12} y={padT + innerH / 2} textAnchor="middle" fontSize="10" fill="#6b7280" fontFamily="monospace"
                transform={`rotate(-90, 12, ${padT + innerH / 2})`}>
                Distance (mm)
              </text>
            )}
            {/* Median reference line */}
            <line x1={padL} y1={yFor(median)} x2={W - padR} y2={yFor(median)} stroke="#10b981" strokeDasharray="3,2" strokeWidth="0.7" opacity="0.7" />
            <text x={W - padR - 2} y={yFor(median) - 2} textAnchor="end" fontSize={expanded ? 10 : 7} fill="#059669" fontFamily="monospace">
              med {usToMm(median).toFixed(1)} mm
            </text>

            {/* X-axis sample indices */}
            {xLabelIndices.map((i) => (
              <g key={i}>
                {expanded && (
                  <line x1={xFor(i)} y1={padT + innerH} x2={xFor(i)} y2={padT + innerH + 3} stroke="#9ca3af" strokeWidth="0.5" />
                )}
                <text x={xFor(i)} y={H - 8} textAnchor="middle" fontSize={expanded ? 9 : 8} fill="#6b7280" fontFamily="monospace">
                  #{i}
                </text>
              </g>
            ))}
            {expanded && (
              <text x={padL + innerW / 2} y={H - 1} textAnchor="middle" fontSize="9" fill="#6b7280" fontFamily="monospace">
                Sample index (0–{rawUs.length - 1})
              </text>
            )}

            {/* Sparkline path */}
            {path && <path d={path} fill="none" stroke="#3b82f6" strokeWidth={expanded ? 1.5 : 1} />}

            {/* Points */}
            {points.map((p) => p.y === null
              ? <circle key={p.i} cx={p.x} cy={padT + innerH / 2} r={ptROut} fill="#dc2626" opacity="0.7" />
              : Math.abs(p.us - mean) > 2 * sd
                ? <circle key={p.i} cx={p.x} cy={p.y} r={ptROut} fill="#f59e0b" />
                : <circle key={p.i} cx={p.x} cy={p.y} r={ptR} fill="#1d4ed8" />
            )}

            {/* Hover crosshair + marker */}
            {hover && hover.y !== null && (
              <>
                <line x1={hover.x} y1={padT} x2={hover.x} y2={padT + innerH} stroke="#1f2937" strokeWidth="0.5" opacity="0.5" />
                <circle cx={hover.x} cy={hover.y} r={expanded ? 6 : 3.5} fill="none" stroke="#1f2937" strokeWidth="1" />
              </>
            )}
            {hover && hover.y === null && (
              <line x1={hover.x} y1={padT} x2={hover.x} y2={padT + innerH} stroke="#dc2626" strokeWidth="0.5" opacity="0.7" />
            )}
          </svg>

          {/* Tooltip */}
          {hover && (
            <div className={`absolute ${expanded ? "top-2 right-2 text-xs" : "top-1 right-1 text-[10px]"} bg-gray-900 text-white font-mono px-2 py-1 rounded shadow pointer-events-none`}>
              <div>idx: <span className="text-amber-300">#{hover.i}</span></div>
              <div>µs : <span className="text-blue-300">{hover.us}</span>{hover.us === 0 && <span className="text-red-400 ml-1">(timeout)</span>}</div>
              <div>mm : <span className="text-green-300">{hover.mm !== null ? hover.mm.toFixed(2) : "—"}</span></div>
              {hover.mm !== null && (
                <div>Δmed: <span className="text-purple-300">{((hover.mm - usToMm(median)) >= 0 ? "+" : "") + (hover.mm - usToMm(median)).toFixed(2)} mm</span></div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 text-[9px] text-gray-500 mt-1 flex-wrap">
            <span><span className="inline-block w-2 h-2 bg-blue-700 rounded-full align-middle mr-1" />sample</span>
            <span><span className="inline-block w-2 h-2 bg-amber-500 rounded-full align-middle mr-1" />outlier &gt;2σ</span>
            <span><span className="inline-block w-2 h-2 bg-red-600 rounded-full align-middle mr-1" />timeout</span>
            <span><span className="inline-block w-3 h-px bg-emerald-600 align-middle mr-1" style={{ borderTop: "1px dashed" }} />median</span>
            {expanded && <span className="text-emerald-700 font-semibold">· hover any point for index, µs, mm, Δ from median</span>}
          </div>
        </div>
      )}

      {view === "histogram" && (
        <div>
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full bg-gray-50 rounded" style={{ height: "180px" }}>
            {bins.map((count, b) => {
              const x = padL + (b / binCount) * (W - padL - padR);
              const w = ((W - padL - padR) / binCount) - 1;
              const h = (count / binMax) * (H - padT - padB);
              const y = H - padB - h;
              const labelMm = (minMm + (b + 0.5) * binW).toFixed(1);
              return (
                <g key={b}>
                  <rect x={x} y={y} width={w} height={h} fill="#3b82f6" opacity="0.7">
                    <title>{`${labelMm} mm: ${count} samples`}</title>
                  </rect>
                  {count > 0 && (
                    <text x={x + w / 2} y={y - 2} textAnchor="middle" fontSize="8" fill="#1f2937" fontFamily="monospace">
                      {count}
                    </text>
                  )}
                </g>
              );
            })}
            {/* X-axis: min / median / max in mm */}
            <text x={padL} y={H - 4} textAnchor="start" fontSize="8" fill="#6b7280" fontFamily="monospace">{minMm.toFixed(1)} mm</text>
            <text x={W / 2} y={H - 4} textAnchor="middle" fontSize="8" fill="#059669" fontFamily="monospace">med {usToMm(median).toFixed(1)}</text>
            <text x={W - padR} y={H - 4} textAnchor="end" fontSize="8" fill="#6b7280" fontFamily="monospace">{maxMm.toFixed(1)} mm</text>
          </svg>
          <div className="text-[9px] text-gray-500 mt-1">
            {binCount} bins across {(maxMm - minMm).toFixed(2)} mm range
          </div>
        </div>
      )}

      {view === "table" && (
        <div className="bg-gray-50 rounded p-2 max-h-64 overflow-y-auto">
          <table className="w-full text-[10px] font-mono">
            <thead className="sticky top-0 bg-gray-100">
              <tr className="text-gray-600">
                <th className="text-left px-1 py-0.5">#</th>
                <th className="text-right px-1 py-0.5">µs</th>
                <th className="text-right px-1 py-0.5">mm</th>
                <th className="text-right px-1 py-0.5">Δ med (mm)</th>
              </tr>
            </thead>
            <tbody>
              {rawUs.map((us, i) => {
                const mm = usToMm(us);
                const isTimeout = us === 0;
                const isOutlier = !isTimeout && Math.abs(us - mean) > 2 * sd;
                return (
                  <tr
                    key={i}
                    className={isTimeout ? "bg-red-50" : isOutlier ? "bg-amber-50" : ""}
                  >
                    <td className="px-1 py-0.5 text-gray-500">{i}</td>
                    <td className="px-1 py-0.5 text-right">{us}</td>
                    <td className="px-1 py-0.5 text-right">{mm !== null ? mm.toFixed(2) : "—"}</td>
                    <td className="px-1 py-0.5 text-right text-gray-500">
                      {mm !== null ? ((mm - usToMm(median)) >= 0 ? "+" : "") + (mm - usToMm(median)).toFixed(2) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] font-mono mt-2">
        <span>n valid</span><span className="text-right">{valids.length}/{rawUs.length}</span>
        <span>min / max</span><span className="text-right">{usToMm(min).toFixed(1)} / {usToMm(max).toFixed(1)} mm</span>
        <span>median</span><span className="text-right">{usToMm(median).toFixed(1)} mm</span>
        <span>spread</span><span className="text-right">{(usToMm(max) - usToMm(min)).toFixed(1)} mm</span>
        <span>σ</span><span className="text-right">{(sd * c_ms / 2000).toFixed(2)} mm</span>
      </div>
    </div>
  );
};

const RichTelemetryPanel = ({
  telemetry,
  telemetryIndex,
  setTelemetryIndex,
  telemetryLimit,
  setTelemetryLimit,
  showRawJson,
  setShowRawJson,
  selectedDevice,
}) => {
  if (!selectedDevice) return null;

  const current = telemetry[telemetryIndex];
  const payload = current?.raw_payload || {};
  // The grouped view also shows fields derived from reported ones (e.g. tilt from the axes).
  const displayed = withDerivedFields(payload);
  const allKeys = Object.keys(payload);
  const groupedKeys = new Set(TELEMETRY_GROUPS.flatMap((g) => g.keys));
  const ungrouped = allKeys.filter((k) => !groupedKeys.has(k));
  const hwVariant = ("ext_t" in payload || "tilt" in payload || "ax" in payload) ? "v0.3" : "v0.2";

  return (
    <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10">
      <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Database className="w-4 h-4 text-on-surface-variant" />
          <h3 className="text-sm font-semibold text-on-surface">Rich Telemetry</h3>
          {current && (
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
              hwVariant === "v0.3" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"
            }`}>
              {hwVariant} ({allKeys.length} fields)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={telemetryLimit}
            onChange={(e) => setTelemetryLimit(parseInt(e.target.value))}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            {[5, 10, 20, 50, 100].map((n) => <option key={n} value={n}>{n} payloads</option>)}
          </select>
          <button
            onClick={() => setShowRawJson(!showRawJson)}
            className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200"
          >
            {showRawJson ? "Grouped" : "Raw JSON"}
          </button>
        </div>
      </div>

      {telemetry.length === 0 ? (
        <div className="p-6 text-center text-gray-500 text-sm">No telemetry payloads available.</div>
      ) : (
        <>
          {/* History timeline */}
          <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-1 overflow-x-auto">
            {telemetry.map((r, i) => {
              const isActive = i === telemetryIndex;
              return (
                <button
                  key={r.id}
                  onClick={() => setTelemetryIndex(i)}
                  className={`flex-shrink-0 px-2 py-1 rounded text-[11px] font-mono transition-colors ${
                    isActive
                      ? "bg-primary text-on-primary"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  title={new Date(r.timestamp).toLocaleString()}
                >
                  #{r.raw_payload?.boot ?? i}
                  <span className="ml-1 opacity-70">{r.raw_payload?.wake?.slice(0, 3) || ""}</span>
                </button>
              );
            })}
          </div>

          {current && (
            <div className="p-4 space-y-4">
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 border-b pb-2">
                <span><strong>Timestamp:</strong> {new Date(current.timestamp).toLocaleString()}</span>
                <span><strong>Received:</strong> {formatRelativeTime(current.received_at)}</span>
                {current.is_backlog && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded font-semibold">BACKLOG</span>
                )}
              </div>

              {showRawJson ? (
                <pre className="bg-gray-900 text-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">
                  {JSON.stringify(payload, null, 2)}
                </pre>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {TELEMETRY_GROUPS.map((group) => {
                    const present = group.keys.filter((k) => k in displayed);
                    if (present.length === 0) return null;
                    const isDistance = group.title === "Distance";
                    const hasRawUs = isDistance && Array.isArray(payload.d1_raw_us);
                    const dlKeys = hasRawUs ? present.filter((k) => k !== "d1_raw_us") : present;
                    return (
                      <div key={group.title} className={`border border-outline-variant/30 rounded-lg p-3 ${hasRawUs ? "md:col-span-2 lg:col-span-3" : ""}`}>
                        <h4 className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant mb-2">
                          {group.title}
                        </h4>
                        <dl className="space-y-1">
                          {dlKeys.map((k) => (
                            <div key={k} className="flex justify-between gap-2 text-xs">
                              <dt className="text-gray-500 truncate" title={k}>{KEY_LABELS[k] || k}</dt>
                              <dd className="font-mono font-medium text-on-surface text-right">
                                {formatTelemetryValue(k, displayed[k])}
                              </dd>
                            </div>
                          ))}
                        </dl>
                        {hasRawUs && <DistanceRawSparkline rawUs={payload.d1_raw_us} payload={payload} />}
                      </div>
                    );
                  })}
                  {ungrouped.length > 0 && (
                    <div className="border border-amber-300/50 rounded-lg p-3 md:col-span-2 lg:col-span-3">
                      <h4 className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-2">
                        Other / Unknown ({ungrouped.length})
                      </h4>
                      <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                        {ungrouped.map((k) => (
                          <div key={k} className="flex justify-between gap-2 text-xs">
                            <dt className="text-gray-500 truncate" title={k}>{k}</dt>
                            <dd className="font-mono font-medium text-on-surface text-right">
                              {formatTelemetryValue(k, payload[k])}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Telemetry Graph Panel
// Plots arbitrary numeric raw_payload fields over a date/time window with
// preset ranges (1h..30d), per-field stats, and outlier highlighting (|z|>2.5).
// Field options are auto-derived from the latest payload so v0.2 vs v0.3
// devices show only fields they actually report.
// ──────────────────────────────────────────────────────────────────────────────

const RANGE_PRESETS = [
  { id: "1h", label: "1h", ms: 60 * 60 * 1000 },
  { id: "6h", label: "6h", ms: 6 * 60 * 60 * 1000 },
  { id: "24h", label: "24h", ms: 24 * 60 * 60 * 1000 },
  { id: "7d", label: "7d", ms: 7 * 24 * 60 * 60 * 1000 },
  { id: "30d", label: "30d", ms: 30 * 24 * 60 * 60 * 1000 },
  { id: "custom", label: "Custom", ms: 0 },
];

const LINE_COLORS = [
  "#2563eb", "#dc2626", "#16a34a", "#ea580c",
  "#9333ea", "#0891b2", "#ca8a04", "#db2777",
];

const DEFAULT_GRAPH_FIELDS = ["ext_t", "t", "bv", "d1"];

const computeStats = (data, field) => {
  const values = data.map((d) => d[field]).filter((v) => typeof v === "number");
  if (values.length === 0) return null;
  const n = values.length;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  const std = Math.sqrt(variance);
  const outliers = std > 0 ? values.filter((v) => Math.abs((v - mean) / std) > 2.5).length : 0;
  return { n, min, max, mean, std, outliers };
};

const toLocalInput = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const TelemetryGraphPanel = ({ selectedDevice, latestPayload, telemetry = [] }) => {
  const [preset, setPreset] = useState("24h");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [selectedFields, setSelectedFields] = useState(DEFAULT_GRAPH_FIELDS);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalInRange, setTotalInRange] = useState(0);
  const [showRawOverlay, setShowRawOverlay] = useState(true);
  const [rawAggStat, setRawAggStat] = useState("median"); // "median" | "min" | "max"
  const [rawSamples, setRawSamples] = useState([]); // [{timestamp, d1_raw_us, ext_t, ext_h}]
  const [rawTotal, setRawTotal] = useState(0);

  // Per-instance raw samples (d1_raw_us) over the selected range — fetched separately
  // from the chart series so the overlay covers the whole time window, not just
  // the small set loaded by Rich Telemetry.
  const rawCandidates = rawSamples;

  // Derive numeric field options from the latest payload.
  const numericFields = useMemo(() => deriveNumericFields(latestPayload), [latestPayload]);

  // Resolve start/end ISO strings from preset or custom inputs.
  const { startISO, endISO } = useMemo(() => {
    if (preset === "custom") {
      return {
        startISO: customStart ? new Date(customStart).toISOString() : null,
        endISO: customEnd ? new Date(customEnd).toISOString() : null,
      };
    }
    const p = RANGE_PRESETS.find((r) => r.id === preset);
    if (!p) return { startISO: null, endISO: null };
    const end = new Date();
    const start = new Date(end.getTime() - p.ms);
    return { startISO: start.toISOString(), endISO: end.toISOString() };
  }, [preset, customStart, customEnd]);

  // Initialize custom inputs to last 24h when user picks "custom".
  useEffect(() => {
    if (preset === "custom" && !customStart && !customEnd) {
      const end = new Date();
      const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
      setCustomStart(toLocalInput(start));
      setCustomEnd(toLocalInput(end));
    }
  }, [preset, customStart, customEnd]);

  const fetchSeries = async () => {
    if (!selectedDevice || selectedFields.length === 0) {
      setData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Derived fields (true tilt) are computed here from their reported sources.
      const res = await getTelemetrySeries(selectedDevice, seriesRequestFields(selectedFields), startISO, endISO, 5000);
      setData(withDerivedSeries(res.data || [], selectedFields));
      setTotalInRange(res.total_in_range || 0);
    } catch (e) {
      setError(e.message || "Failed to load series");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when device, fields, or range changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSeries(); }, [selectedDevice, selectedFields.join(","), startISO, endISO]);

  // Fetch per-wake raw d1 samples for the same range as the chart.
  // NOTE: do not depend on showRawOverlay here \u2014 we keep the samples loaded so
  // the toggle chip stays visible (the chip's presence is gated on
  // rawCandidates.length > 0). The overlay rendering itself is what reacts to
  // showRawOverlay.
  useEffect(() => {
    if (!selectedDevice) {
      setRawSamples([]);
      setRawTotal(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getRawSamples(selectedDevice, startISO, endISO, 5000);
        if (cancelled) return;
        setRawSamples(res.data || []);
        setRawTotal(res.total_in_range || 0);
      } catch (e) {
        if (!cancelled) {
          setRawSamples([]);
          setRawTotal(0);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDevice, startISO, endISO]);

  const toggleField = (key) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };

  // Per-field stats
  const stats = useMemo(() => {
    const out = {};
    selectedFields.forEach((f) => { out[f] = computeStats(data, f); });
    return out;
  }, [data, selectedFields]);

  // Merge time-series data with per-wake raw samples (50 dots stacked at each
  // wake's exact timestamp). Uses a numeric `t` field so the XAxis (type=number,
  // scale=time) collapses duplicates onto the same X position.
  // Per-wake aggregate of the raw d1 samples: median + (min,max) spread.
  // Rendering one summary marker per wake (with an ErrorBar showing the
  // min→max range) is dramatically faster and far easier to read than
  // splatting all 50 individual sample dots per wake.
  const rawAggregates = useMemo(() => {
    if (!showRawOverlay || rawCandidates.length === 0) return [];
    const out = [];
    rawCandidates.forEach((r) => {
      const __t = Date.parse(r.timestamp);
      // validSamples() drops 0us entries (the ultrasonic sensor's
      // timeout/no-echo sentinel) before converting to cm - including them
      // used to drag min/median toward zero on any wake with a timeout.
      const valid = validSamples(r.d1_raw_us);
      if (!valid.length) return;
      const c_ms = speedOfSound(r.ext_t, r.ext_h);
      const cms = valid.map((us) => usToCm(us, c_ms));
      cms.sort((a, b) => a - b);
      const n = cms.length;
      const min = cms[0];
      const max = cms[n - 1];
      const p50 = n % 2 ? cms[(n - 1) >> 1] : (cms[n / 2 - 1] + cms[n / 2]) / 2;
      const center = rawAggStat === "min" ? min : rawAggStat === "max" ? max : p50;
      out.push({
        __t,
        d1_raw_center: center,
        d1_raw_p50: p50,
        d1_raw_min: min,
        d1_raw_max: max,
        // ErrorBar with array dataKey expects [errorBelow, errorAbove].
        d1_raw_err: [center - min, max - center],
        d1_raw_spread: max - min,
        d1_raw_n: n,
      });
    });
    return out;
  }, [rawCandidates, showRawOverlay, rawAggStat]);

  // p95 of per-wake spread — used to map dot color on a tight→noisy ramp
  // without a single outlier wake compressing the whole scale.
  const rawSpreadP95 = useMemo(() => {
    if (rawAggregates.length === 0) return 0;
    const spreads = rawAggregates.map((a) => a.d1_raw_spread).sort((a, b) => a - b);
    const idx = Math.min(spreads.length - 1, Math.floor(spreads.length * 0.95));
    return spreads[idx] || 0;
  }, [rawAggregates]);

  // Rolling envelope of (lo = min of mins, hi = max of maxes) across ~200
  // chunks of consecutive wakes. (Currently unused: the Area-based spread
  // band was visually misleading because it linearly interpolated across
  // gaps and let single outlier wakes balloon the whole region. Kept around
  // in case we revisit a smoothed-band rendering later.)
  // eslint-disable-next-line no-unused-vars
  const rawEnvelope = useMemo(() => {
    const n = rawAggregates.length;
    if (n === 0) return [];
    const targetSteps = 200;
    const chunk = Math.max(1, Math.ceil(n / targetSteps));
    const out = [];
    for (let i = 0; i < n; i += chunk) {
      let lo = Infinity, hi = -Infinity, tSum = 0, c = 0;
      const end = Math.min(n, i + chunk);
      for (let j = i; j < end; j++) {
        const a = rawAggregates[j];
        if (a.d1_raw_min < lo) lo = a.d1_raw_min;
        if (a.d1_raw_max > hi) hi = a.d1_raw_max;
        tSum += a.__t; c++;
      }
      out.push({ __t: tSum / c, d1_raw_band: [lo, hi] });
    }
    return out;
  }, [rawAggregates]);

  // Whiskers (per-wake min/max ErrorBar) are always drawn now. Tested up to
  // ~2000 wakes — the bars get tightly packed but still convey real per-wake
  // variance, and dropping the misleading Area band was a bigger UX win.

  // Custom dot renderer that tints by per-wake spread (tight = light purple,
  // noisy = red). Gives a quick visual signal of where the sensor was
  // unreliable, which is what you'd want when designing an algorithm.
  const rawDotColor = (spread) => {
    if (!rawSpreadP95) return "#a855f7";
    const t = Math.max(0, Math.min(1, spread / rawSpreadP95));
    // interpolate light-purple #c4b5fd → magenta-red #dc2626
    const lerp = (a, b) => Math.round(a + (b - a) * t);
    const r = lerp(0xc4, 0xdc), g = lerp(0xb5, 0x26), b = lerp(0xfd, 0x26);
    return `rgb(${r},${g},${b})`;
  };
  const renderRawDot = (props) => {
    const spread = props.payload?.d1_raw_spread ?? 0;
    return (
      <circle
        cx={props.cx}
        cy={props.cy}
        r={1.0}
        fill={rawDotColor(spread)}
        fillOpacity={0.7}
        stroke="none"
      />
    );
  };

  // Pre-compute __t on chart base data once. No raw-point interleaving here
  // — the overlay Line gets its own `data` prop.
  const chartData = useMemo(
    () => (data || []).map((row) => ({ ...row, __t: Date.parse(row.timestamp) })),
    [data]
  );

  const totalRawDots = useMemo(
    () => rawCandidates.reduce((acc, r) => acc + (r.d1_raw_us?.length || 0), 0),
    [rawCandidates]
  );

  // Build outlier dot renderers per field for anomaly visualization.
  const renderOutlierDot = (field) => (props) => {
    const s = stats[field];
    const v = props.payload?.[field];
    if (!s || s.std === 0 || typeof v !== "number") return null;
    const z = Math.abs((v - s.mean) / s.std);
    if (z <= 2.5) return null;
    return <circle cx={props.cx} cy={props.cy} r={4} fill="#dc2626" stroke="#fff" strokeWidth={1} />;
  };

  // ---- CSV export helpers --------------------------------------------------
  const downloadCsv = (filename, rows) => {
    if (!rows.length) return;
    const cols = Array.from(
      rows.reduce((set, r) => {
        Object.keys(r).forEach((k) => set.add(k));
        return set;
      }, new Set())
    );
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      cols.join(","),
      ...rows.map((r) => cols.map((c) => escape(r[c])).join(",")),
    ].join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const safeMac = (selectedDevice || "device").replace(/[:]/g, "");
  const stamp = `${startISO.replace(/[:.]/g, "-")}_${endISO.replace(/[:.]/g, "-")}`;

  const exportTelemetry = () => {
    const rows = (data || []).map((row) => {
      const { __t, ...rest } = row;
      return rest;
    });
    downloadCsv(`padihat_${safeMac}_telemetry_${stamp}.csv`, rows);
  };

  const [exportingRaw, setExportingRaw] = useState(false);
  const exportRaw = async () => {
    if (exportingRaw) return;
    setExportingRaw(true);
    try {
      // Pull the FULL (un-downsampled) dataset for CSV export so the user
      // gets every wake in the range, not just the chart-stride preview.
      let wakes = rawCandidates;
      try {
        const res = await getRawSamples(selectedDevice, startISO, endISO, 20000, true);
        if (Array.isArray(res?.data) && res.data.length) wakes = res.data;
      } catch (_) {
        /* fall back to whatever the chart already has */
      }
      const rows = [];
      wakes.forEach((r) => {
        const arr = r.d1_raw_us || [];
        const ext_t = typeof r.ext_t === "number" ? r.ext_t : null;
        const ext_h = typeof r.ext_h === "number" ? r.ext_h : 0;
        const c_ms = ext_t != null
          ? 331.3 * Math.sqrt(1 + ext_t / 273.15) + 0.0124 * ext_h
          : 343;
        arr.forEach((us, idx) => {
          const cm = typeof us === "number" && isFinite(us)
            ? (us * c_ms) / 2 / 10000
            : null;
          rows.push({
            timestamp: r.timestamp,
            boot: r.boot ?? "",
            wake: r.wake ?? "",
            ext_t: ext_t ?? "",
            ext_h: r.ext_h ?? "",
            c_ms: c_ms.toFixed(3),
            sample_idx: idx,
            d1_raw_us: us,
            d1_raw_cm: cm != null ? cm.toFixed(3) : "",
          });
        });
      });
      downloadCsv(`padihat_${safeMac}_raw_${stamp}.csv`, rows);
    } finally {
      setExportingRaw(false);
    }
  };

  if (!selectedDevice) return null;

  return (
    <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10">
      <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <LineChartIcon className="w-4 h-4 text-on-surface-variant" />
          <h3 className="text-sm font-semibold text-on-surface">Telemetry Graphs</h3>
          {data.length > 0 && (
            <span className="text-[10px] text-gray-500">
              {data.length} pts {totalInRange > data.length && `(of ${totalInRange} in range)`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportTelemetry}
            disabled={data.length === 0}
            title="Export the loaded telemetry series as CSV (one row per timestamp)"
            className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-1 disabled:opacity-40"
          >
            <Download className="w-3 h-3" />
            Telemetry CSV
          </button>
          <button
            onClick={exportRaw}
            disabled={rawCandidates.length === 0 || exportingRaw}
            title="Export per-wake raw d1 samples as CSV (fetches the FULL un-downsampled range; one row per individual ranging attempt)"
            className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded hover:bg-purple-100 flex items-center gap-1 disabled:opacity-40"
          >
            <Download className={`w-3 h-3 ${exportingRaw ? "animate-pulse" : ""}`} />
            {exportingRaw ? "Exporting…" : "Raw samples CSV"}
          </button>
          <button
            onClick={fetchSeries}
            disabled={loading}
            className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Range selector */}
      <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap items-center gap-2">
        <Calendar className="w-3.5 h-3.5 text-gray-500" />
        <span className="text-xs text-gray-600 font-medium">Range:</span>
        {RANGE_PRESETS.map((r) => (
          <button
            key={r.id}
            onClick={() => setPreset(r.id)}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              preset === r.id ? "bg-primary text-on-primary" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {r.label}
          </button>
        ))}
        {preset === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <input
              type="datetime-local"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1"
            />
            <span className="text-xs text-gray-500">→</span>
            <input
              type="datetime-local"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1"
            />
          </div>
        )}
      </div>

      {/* Field chips, grouped by category (matches Rich Telemetry layout) */}
      <div className="px-4 py-2 border-b border-gray-100">
        <div className="flex items-start gap-2">
          <Activity className="w-3.5 h-3.5 text-gray-500 mt-1 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {numericFields.length === 0 ? (
              <span className="text-xs text-gray-400">No numeric fields available — load telemetry first.</span>
            ) : (
              <div className="space-y-1.5">
                {(() => {
                  const available = new Set(numericFields);
                  const seen = new Set();
                  const groups = TELEMETRY_GROUPS
                    .map((g) => {
                      const keys = g.keys.filter((k) => available.has(k));
                      keys.forEach((k) => seen.add(k));
                      return { title: g.title, keys };
                    })
                    .filter((g) => g.keys.length > 0);
                  // Inject synthetic 'd1_raw' chip into Distance group when raw samples are available.
                  if (rawCandidates.length > 0) {
                    const dist = groups.find((g) => g.title.toLowerCase() === "distance");
                    if (dist && !dist.keys.includes("d1_raw")) dist.keys.push("d1_raw");
                    else if (!dist) groups.push({ title: "Distance", keys: ["d1_raw"] });
                  }
                  const other = numericFields.filter((k) => !seen.has(k));
                  if (other.length) groups.push({ title: "Other", keys: other });
                  return groups.map((g) => (
                    <div key={g.title} className="flex items-start gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold w-28 flex-shrink-0 pt-1">
                        {g.title}
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {g.keys.map((k) => {
                          if (k === "d1_raw") {
                            const active = showRawOverlay;
                            return (
                              <span key={k} className="inline-flex items-center gap-1">
                                <button
                                  onClick={() => setShowRawOverlay((v) => !v)}
                                  className={`text-[11px] font-mono px-2 py-0.5 rounded border transition-colors ${
                                    active ? "text-white border-transparent" : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                                  }`}
                                  style={active ? { backgroundColor: "#a855f7" } : {}}
                                  title={`${totalRawDots} samples across ${rawCandidates.length} wake${rawCandidates.length === 1 ? "" : "s"}${rawTotal > rawCandidates.length ? ` (of ${rawTotal} in range)` : ""}`}
                                >
                                  {KEY_LABELS[k]}
                                </button>
                                {active && (
                                  <select
                                    value={rawAggStat}
                                    onChange={(e) => setRawAggStat(e.target.value)}
                                    title="Aggregate stat for the raw overlay marker (spread bar still shows full min\u2192max)"
                                    className="text-[11px] font-mono px-1 py-0.5 rounded border border-purple-300 bg-white text-purple-700"
                                  >
                                    <option value="median">median</option>
                                    <option value="min">min</option>
                                    <option value="max">max</option>
                                  </select>
                                )}
                              </span>
                            );
                          }
                          const active = selectedFields.includes(k);
                          const idx = selectedFields.indexOf(k);
                          const color = active ? LINE_COLORS[idx % LINE_COLORS.length] : "transparent";
                          return (
                            <button
                              key={k}
                              onClick={() => toggleField(k)}
                              className={`text-[11px] font-mono px-2 py-0.5 rounded border transition-colors ${
                                active ? "text-white border-transparent" : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                              }`}
                              style={active ? { backgroundColor: color } : {}}
                              title={k}
                            >
                              {KEY_LABELS[k] || k}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-4">
        {error ? (
          <div className="text-sm text-red-600 text-center py-8">{error}</div>
        ) : loading && data.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-8">Loading…</div>
        ) : data.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-8">No data in selected range.</div>
        ) : selectedFields.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-8">Select at least one field to chart.</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="__t"
                  type="number"
                  scale="time"
                  domain={[Date.parse(startISO), Date.parse(endISO)]}
                  allowDataOverflow
                  tick={{ fontSize: 10 }}
                  tickFormatter={(t) => {
                    const d = new Date(t);
                    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                  }}
                  minTickGap={40}
                />
                <YAxis tick={{ fontSize: 10 }} />
                <RechartsTooltip
                  labelFormatter={(t) => new Date(t).toLocaleString()}
                  contentStyle={{ fontSize: "11px" }}
                />
                <Legend wrapperStyle={{ fontSize: "11px" }} />
                {/* Raw overlay rendered FIRST so the main telemetry lines
                    paint on top and remain readable. */}
                {showRawOverlay && rawAggregates.length > 0 && (
                  <Line
                    key={`d1_raw_${rawAggStat}`}
                    data={rawAggregates}
                    type="linear"
                    dataKey="d1_raw_center"
                    name={`Dist 1 raw (${rawAggStat}, ${rawAggregates.length} wakes / ${totalRawDots} samples \u00b1 spread)`}
                    stroke="transparent"
                    strokeWidth={0}
                    dot={renderRawDot}
                    activeDot={{ r: 4, fill: "#7e22ce" }}
                    legendType="circle"
                    isAnimationActive={false}
                  >
                    <ErrorBar
                      dataKey="d1_raw_err"
                      width={2}
                      strokeWidth={1}
                      stroke="#a855f7"
                      strokeOpacity={0.4}
                      direction="y"
                    />
                  </Line>
                )}
                {selectedFields.map((f, i) => (
                  <Line
                    key={f}
                    type="monotone"
                    dataKey={f}
                    name={KEY_LABELS[f] || f}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    dot={renderOutlierDot(f)}
                    activeDot={{ r: 4 }}
                    strokeWidth={1.5}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>

            {/* Stats */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              {selectedFields.map((f, i) => {
                const s = stats[f];
                if (!s) return null;
                const color = LINE_COLORS[i % LINE_COLORS.length];
                return (
                  <div key={f} className="border border-outline-variant/30 rounded-lg p-2 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                        {KEY_LABELS[f] || f}
                      </span>
                      {s.outliers > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-bold">
                          {s.outliers} outlier{s.outliers !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 font-mono text-gray-600">
                      <span>n: {s.n}</span>
                      <span>μ: {s.mean.toFixed(2)}</span>
                      <span>min: {s.min.toFixed(2)}</span>
                      <span>max: {s.max.toFixed(2)}</span>
                      <span className="col-span-2">σ: {s.std.toFixed(3)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Red dots mark points where |z-score| &gt; 2.5 (likely anomalies).
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const ESP32DebugDashboard = () => {
  const { devices, selectedDevice, setSelectedDevice } = useDevices();

  const [diagnostics, setDiagnostics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [systemOverview, setSystemOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);

  const [showConfig, setShowConfig] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [compactView, setCompactView] = useState(false);
  const [logLimit, setLogLimit] = useState(50);
  const [expandedLogs, setExpandedLogs] = useState(new Set([0]));

  // Rich telemetry viewer state
  const [telemetry, setTelemetry] = useState([]);
  const [telemetryLimit, setTelemetryLimit] = useState(10);
  const [telemetryIndex, setTelemetryIndex] = useState(0);
  const [showRawJson, setShowRawJson] = useState(false);

  useEffect(() => {
    if (autoRefresh && selectedDevice) {
      const interval = setInterval(() => fetchAllData(), refreshInterval * 1000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshInterval, selectedDevice]);

  const parseLogContent = (logContent, level) => {
    if (!logContent) return null;

    // --- Compact structured event format (new firmware) ---
    // log_content is like: "+4200ms NET_OK" or "+5100ms MQTT_FAIL"
    // level field from DB tells us severity
    const compactMatch = logContent.match(/^\+(\d+)ms\s+(\S+)$/);
    if (compactMatch) {
      return {
        type: "event",
        ms: parseInt(compactMatch[1]),
        code: compactMatch[2],
        level: level || "INFO",
      };
    }

    // --- Legacy plain-text format (old firmware) ---
    const headerMatch = logContent.match(/Padihat Field Node v([\d.]+)\s+Board:\s+(.+?)\s+=/);
    const version = headerMatch ? headerMatch[1] : null;
    const board = headerMatch ? headerMatch[2] : null;

    const heapMatch = logContent.match(/\[HEAP\]\s+Free:\s+([\d]+)\s+bytes,\s+Min Free:\s+([\d]+)\s+bytes/);
    const heapFree = heapMatch ? parseInt(heapMatch[1]) : null;
    const heapMinFree = heapMatch ? parseInt(heapMatch[2]) : null;

    const statusItems = [];
    const statusRegex = /\[([^\]]+)\]\s+(OK|FAILED|ERROR|WARNING|[^\n\[]+)/g;
    let match;
    while ((match = statusRegex.exec(logContent)) !== null) {
      if (match[1] !== "HEAP") {
        statusItems.push({
          component: match[1],
          status: match[2],
          isOk:      match[2] === "OK",
          isFailed:  match[2] === "FAILED" || match[2] === "ERROR",
          isWarning: match[2] === "WARNING",
        });
      }
    }

    return { type: "legacy", version, board, heapFree, heapMinFree, statusItems, rawContent: logContent, hasIssues: statusItems.some((i) => i.isFailed || i.isWarning) };
  };

  const fetchSystemOverviewData = async () => {
    try {
      const data = await getSystemOverview();
      setSystemOverview(data);
    } catch (err) {
      console.error("Error fetching system overview:", err);
      setError(err.message);
    }
  };

  const fetchDiagnosticsData = async (deviceMac) => {
    try {
      const data = await getDiagnostics(deviceMac);
      setDiagnostics(data);
      return true;
    } catch (err) {
      console.error("Error fetching diagnostics:", err);
      setDiagnostics(null);
      return false;
    }
  };

  const fetchLogsData = async (deviceMac) => {
    try {
      const data = await getLogs(deviceMac, logLimit);
      setLogs(data.logs || []);
      return true;
    } catch (err) {
      console.error("Error fetching logs:", err);
      setLogs([]);
      return false;
    }
  };

  const fetchTelemetryData = async (deviceMac) => {
    try {
      const data = await getTelemetry(deviceMac, telemetryLimit);
      setTelemetry(data.readings || []);
      setTelemetryIndex(0);
      return true;
    } catch (err) {
      console.error("Error fetching telemetry:", err);
      setTelemetry([]);
      return false;
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchSystemOverviewData();
      if (selectedDevice) {
        await Promise.all([
          fetchDiagnosticsData(selectedDevice),
          fetchLogsData(selectedDevice),
          fetchTelemetryData(selectedDevice),
        ]);
      }
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDevice) {
      fetchDiagnosticsData(selectedDevice);
      fetchLogsData(selectedDevice);
      fetchTelemetryData(selectedDevice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice, logLimit, telemetryLimit]);

  const formatBytes = (bytes) => {
    if (!bytes) return "N/A";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  const toggleLogExpansion = (index) => {
    const next = new Set(expandedLogs);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setExpandedLogs(next);
  };

  const filteredLogs = logs
    .filter((log) => !searchTerm || log.log_content?.toLowerCase().includes(searchTerm.toLowerCase()))
    .map((log) => ({ ...log, parsed: parseLogContent(log.log_content, log.level) }));

  if (loading && !diagnostics) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <Bug className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
          <p className="text-on-surface-variant">Loading debug data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <DebugConfigModal
        isOpen={showConfig}
        onClose={() => setShowConfig(false)}
        onApply={() => { setShowConfig(false); fetchAllData(); }}
        logLimit={logLimit}
        onLogLimitChange={setLogLimit}
        autoRefresh={autoRefresh}
        onAutoRefreshChange={setAutoRefresh}
        refreshInterval={refreshInterval}
        onIntervalChange={setRefreshInterval}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold font-headline text-on-surface tracking-tight">System Diagnostics</h1>
            <p className="text-on-surface-variant mt-1">Real-time telemetry and process logging for field sensor nodes</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="min-w-[180px]">
              <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest mb-1">Active Node</p>
              <DeviceSelector
                devices={devices}
                value={selectedDevice}
                onChange={setSelectedDevice}
              />
            </div>
            <div className="flex items-center gap-1.5 mt-4">
              {autoRefresh && <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
              {lastUpdate && (
                <span className="text-xs text-on-surface-variant">
                  {formatRelativeTime(lastUpdate.getTime())}
                </span>
              )}
              <button
                onClick={() => setShowConfig(true)}
                aria-label="Debug configuration"
                className="p-1.5 text-on-surface-variant hover:text-on-surface hover:bg-surface-container rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4" />
              </button>
              <button
                onClick={fetchAllData}
                disabled={loading}
                className="px-3 py-1.5 bg-primary text-on-primary text-sm rounded-full hover:bg-primary-container disabled:opacity-50 flex items-center gap-1.5 transition-colors"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
        <AlertBanner type="error" message={error} onDismiss={() => setError(null)} />

        {/* System Overview */}
        {systemOverview && (
          <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 p-4">
            <h3 className="text-sm font-semibold text-on-surface mb-3">System Overview</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
              {[
                { label: "Total Devices",  value: systemOverview.devices.total,                   color: "" },
                { label: "Online (1h)",    value: systemOverview.devices.online_1h,               color: "text-green-600" },
                { label: "Offline",        value: systemOverview.devices.offline,                 color: "text-on-surface-variant" },
                { label: "Total Readings", value: systemOverview.readings.total?.toLocaleString(), color: "" },
                { label: "Readings (24h)", value: systemOverview.readings.last_24h?.toLocaleString(), color: "text-blue-600" },
                { label: "Low Battery",    value: systemOverview.alerts.low_battery_count,        color: "text-red-600" },
              ].map(({ label, value, color }) => (
                <div key={label}>
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className={`font-medium text-lg ${color}`}>{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Device Diagnostics */}
        {diagnostics && (
          <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10 p-4">
            <h3 className="text-sm font-semibold text-on-surface mb-3">
              Device Diagnostics – {diagnostics.device.name || diagnostics.device.mac_address}
            </h3>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-sm mb-4">
              <div>
                <p className="text-xs text-gray-500">Health Status</p>
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getHealthStatusColor(diagnostics.health_status)}`}>
                  {diagnostics.health_status?.toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500">Battery</p>
                <p className="font-medium">{diagnostics.latest_reading?.battery_voltage ? `${diagnostics.latest_reading.battery_voltage.toFixed(2)}V` : "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Temperature</p>
                <p className="font-medium">{diagnostics.latest_reading?.temperature ? `${diagnostics.latest_reading.temperature.toFixed(1)}°C` : "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Humidity</p>
                <p className="font-medium">{diagnostics.latest_reading?.humidity ? `${diagnostics.latest_reading.humidity.toFixed(1)}%` : "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Uptime</p>
                <p className="font-medium">{formatUptime(diagnostics.latest_reading?.uptime_seconds)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Last Seen</p>
                <p className="font-medium text-xs">{diagnostics.latest_reading?.timestamp ? formatRelativeTime(diagnostics.latest_reading.timestamp) : "N/A"}</p>
              </div>
            </div>

            {diagnostics.statistics_24h && (
              <div className="border-t pt-3">
                <h4 className="text-xs font-semibold text-gray-700 mb-2">24-Hour Statistics</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div><p className="text-gray-500">Avg Temperature</p><p className="font-medium">{diagnostics.statistics_24h.avg_temp ? `${diagnostics.statistics_24h.avg_temp.toFixed(1)}°C` : "N/A"}</p></div>
                  <div><p className="text-gray-500">Avg Battery</p><p className="font-medium">{diagnostics.statistics_24h.avg_battery ? `${diagnostics.statistics_24h.avg_battery.toFixed(2)}V` : "N/A"}</p></div>
                  <div><p className="text-gray-500">Min Battery</p><p className="font-medium text-red-600">{diagnostics.statistics_24h.min_battery ? `${diagnostics.statistics_24h.min_battery.toFixed(2)}V` : "N/A"}</p></div>
                  <div><p className="text-gray-500">Total Readings</p><p className="font-medium">{diagnostics.statistics_24h.total_readings || 0}</p></div>
                </div>
              </div>
            )}

            {diagnostics.calibration && (
              <div className="border-t pt-3 mt-3">
                <h4 className="text-xs font-semibold text-gray-700 mb-2">Calibration Settings</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div><p className="text-gray-500">Sensor 1 Cal</p><p className="font-medium">{diagnostics.calibration.distance_1_calibration} cm</p></div>
                  <div><p className="text-gray-500">Sensor 1 Offset</p><p className="font-medium">{diagnostics.calibration.distance_1_offset} cm</p></div>
                  <div><p className="text-gray-500">Sensor 2 Cal</p><p className="font-medium">{diagnostics.calibration.distance_2_calibration} cm</p></div>
                  <div><p className="text-gray-500">Sensor 2 Offset</p><p className="font-medium">{diagnostics.calibration.distance_2_offset} cm</p></div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Rich Telemetry Viewer */}
        <RichTelemetryPanel
          telemetry={telemetry}
          telemetryIndex={telemetryIndex}
          setTelemetryIndex={setTelemetryIndex}
          telemetryLimit={telemetryLimit}
          setTelemetryLimit={setTelemetryLimit}
          showRawJson={showRawJson}
          setShowRawJson={setShowRawJson}
          selectedDevice={selectedDevice}
        />

        {/* Telemetry Graphs */}
        <TelemetryGraphPanel
          selectedDevice={selectedDevice}
          latestPayload={telemetry[0]?.raw_payload}
          telemetry={telemetry}
        />

        {/* Device Logs */}
        <div className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/10">
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-on-surface">Device Logs ({filteredLogs.length})</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCompactView(!compactView)}
                  className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200"
                >
                  {compactView ? "Detailed" : "Compact"}
                </button>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-1"
                >
                  <Filter className="w-3 h-3" />
                  Filters
                  {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="relative">
                  <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search logs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-7 pr-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="p-4">
            {filteredLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
                <p className="font-medium text-green-700">No issues logged</p>
                <p className="text-sm mt-1">
                  {selectedDevice
                    ? "All recent cycles completed cleanly — logs are only sent when a warning or error occurs."
                    : "Please select a device"}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredLogs.map((log, index) => {
                  const isExpanded = expandedLogs.has(index);
                  const parsed = log.parsed;

                  // Compact structured event (new firmware format)
                  if (parsed?.type === "event") {
                    const levelStyles = {
                      ERROR:   "border-l-red-500 bg-red-50 text-red-800",
                      WARNING: "border-l-amber-500 bg-amber-50 text-amber-800",
                      INFO:    "border-l-blue-400 bg-blue-50 text-blue-800",
                    };
                    const style = levelStyles[parsed.level] || levelStyles.INFO;
                    return (
                      <div key={index} className={`border-l-4 ${style} p-3 rounded-r flex items-center justify-between gap-3`}>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold uppercase">{parsed.level.slice(0, 1)}</span>
                          <span className="font-mono text-sm font-semibold">{parsed.code}</span>
                          <span className="text-xs opacity-60">+{(parsed.ms / 1000).toFixed(1)}s</span>
                        </div>
                        <p className="text-xs text-gray-500 flex-shrink-0">{formatRelativeTime(log.received_at)}</p>
                      </div>
                    );
                  }

                  // Raw / unparsed
                  if (!parsed) {
                    return (
                      <div key={index} className="border-l-4 border-l-blue-500 bg-blue-50 p-3 rounded-r">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm text-on-surface break-words font-mono flex-1">{log.log_content}</p>
                          <p className="text-xs text-gray-500 flex-shrink-0">{formatRelativeTime(log.received_at)}</p>
                        </div>
                      </div>
                    );
                  }

                  // Legacy plain-text format
                  return (
                    <div key={index} className={`border rounded-lg ${parsed.hasIssues ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"}`}>
                      <div className="p-3 cursor-pointer hover:bg-opacity-80" onClick={() => toggleLogExpansion(index)}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1 flex-wrap">
                            <button className="text-on-surface-variant hover:text-on-surface flex-shrink-0">
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            <div className="flex items-center gap-2">
                              <Cpu className="w-4 h-4 text-on-surface-variant" />
                              <div>
                                <p className="text-sm font-semibold text-on-surface">{parsed.board || "Unknown Board"}</p>
                                <p className="text-xs text-on-surface-variant">v{parsed.version || "N/A"}</p>
                              </div>
                            </div>
                            {parsed.heapFree && (
                              <div className="flex items-center gap-2">
                                <HardDrive className="w-4 h-4 text-blue-600" />
                                <div>
                                  <p className="text-xs text-on-surface-variant">Free: {formatBytes(parsed.heapFree)}</p>
                                  <p className="text-xs text-gray-500">Min: {formatBytes(parsed.heapMinFree)}</p>
                                </div>
                              </div>
                            )}
                            <div className="flex items-center gap-1 ml-auto">
                              {parsed.hasIssues ? (
                                <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                                  <AlertCircle className="w-3 h-3" />Issues
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                  <CheckCircle className="w-3 h-3" />All OK
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right text-xs text-gray-500 ml-4 flex-shrink-0">
                            {formatRelativeTime(log.received_at)}
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t px-3 pb-3">
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mt-3">
                            {parsed.statusItems.map((item, idx) => (
                              <div
                                key={idx}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
                                  item.isOk ? "bg-green-100 text-green-800" :
                                  item.isFailed ? "bg-red-100 text-red-800" :
                                  item.isWarning ? "bg-yellow-100 text-yellow-800" :
                                  "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {item.isOk ? <CheckCircle className="w-3 h-3" /> :
                                 item.isFailed ? <XCircle className="w-3 h-3" /> :
                                 item.isWarning ? <AlertTriangle className="w-3 h-3" /> :
                                 <Info className="w-3 h-3" />}
                                <span className="font-medium truncate">{item.component}</span>
                              </div>
                            ))}
                          </div>

                          {!compactView && (
                            <div className="mt-3 p-2 bg-gray-900 text-gray-100 rounded text-xs font-mono overflow-x-auto">
                              <pre className="whitespace-pre-wrap">{parsed.rawContent}</pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default ESP32DebugDashboard;
